import {
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    DocumentData,
    documentId,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';

import { IUser } from '@customTypes/user';
import { ChildNewEditFormValues } from 'components/children/ChildNewEditForm';
import { auth, db } from 'external/firebase';

import { addStudentToGroup, getGroupByProductId } from 'external/firebase/firestore/collections/groups';

export const addChildren = async (parentId: string, children: ChildNewEditFormValues) => {
    const docRef = doc(db, 'users', parentId, 'children', children.email.toLowerCase());
    await setDoc(docRef, { ...children, status: 'invited' });
};

export const updateChildren = async (parentId: string, children: ChildNewEditFormValues, currentEmail?: string) => {
    const docRef = doc(db, 'users', parentId, 'children', children.email.toLowerCase());
    // check if document exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        await setDoc(docRef, { ...children, status: 'invited' });
        await deleteDoc(doc(db, 'users', parentId, 'children', currentEmail?.toLowerCase() || ''));
        return;
    }
    await updateDoc(docRef, { ...children });
};

export const assignChildToSubscription = async (
    parentId: string,
    childId: string,
    childEmail: string,
    subscriptionId: string,
) => {
    const parentChildrenDocRef = doc(db, 'users', parentId, 'children', childEmail.toLowerCase());

    try {
        await Promise.all([
            (async () => {
                const docSnap = await getDoc(parentChildrenDocRef);

                if (!docSnap.exists()) {
                    const childUserDocRef = doc(db, 'users', childId);
                    const childUserSnap = await getDoc(childUserDocRef);
                    const childUserData = childUserSnap.exists() ? childUserSnap.data() : {};

                    await setDoc(parentChildrenDocRef, {
                        id: childId,
                        email: childEmail,
                        firstName: childUserData.firstName || '',
                        lastName: childUserData.lastName || '',
                        birthDate: childUserData.birthDate || null,
                        status: 'registered',
                        subscriptions: [subscriptionId],
                    });
                } else {
                    const existingData = docSnap.data();
                    const currentSubscriptions = existingData.subscriptions || [];

                    if (!currentSubscriptions.includes(subscriptionId)) {
                        await updateDoc(parentChildrenDocRef, {
                            subscriptions: arrayUnion(subscriptionId),
                        });
                    }
                }
            })(),
            addChildToGroupBasedOnSubscriptionId(parentId, childId, subscriptionId),
        ]);
    } catch (error: any) {
        throw error;
    }
};

export const addChildToGroupBasedOnSubscriptionId = async (
    parentId: string,
    childId: string,
    subscriptionId: string,
) => {
    const subscriptionDocRef = doc(db, 'users', parentId, 'subscriptions', subscriptionId);
    const subscriptionDocSnap = await getDoc(subscriptionDocRef);

    if (!subscriptionDocSnap.exists()) {
        throw new Error('db/no-document');
    }

    const subscriptionData = subscriptionDocSnap.data();
    const group = await getGroupByProductId(subscriptionData.product.id);

    if (!group) {
        return;
    }

    const childUserDocRef = doc(db, 'users', childId);

    await Promise.all([
        updateDoc(childUserDocRef, {
            groups: arrayUnion(group.id),
        }),
        addStudentToGroup(childId, group.id),
    ]);
};

async function getUnassignedSubscriptions(parentId: string) {
    const subscriptionsRef = collection(db, 'users', parentId, 'subscriptions');
    const subscriptionsSnapshot = await getDocs(subscriptionsRef);

    const allSubscriptionIds = subscriptionsSnapshot.docs.map(doc => doc.id);

    // Step 2: Get all assigned subscription IDs from children
    const childrenRef = collection(db, 'users', parentId, 'children');
    const childrenSnapshot = await getDocs(childrenRef);

    const assignedSubscriptionIdsSet = new Set(
        childrenSnapshot.docs.flatMap(childDoc => childDoc.data().subscriptions || []),
    );

    return allSubscriptionIds.filter(id => !assignedSubscriptionIdsSet.has(id));
}

async function findSubscriptionByChildGroups(parentId: string, childGroups: string[]): Promise<string | null> {
    const subscriptionsRef = collection(db, 'users', parentId, 'subscriptions');
    const subscriptionsSnapshot = await getDocs(subscriptionsRef);

    for (const subscriptionDoc of subscriptionsSnapshot.docs) {
        const subscriptionData = subscriptionDoc.data();

        if (subscriptionData.product?.id) {
            try {
                const group = await getGroupByProductId(subscriptionData.product.id);

                if (group && childGroups.includes(group.id)) {
                    return subscriptionDoc.id;
                }
            } catch (error) {
                console.log(`Error getting group for product ${subscriptionData.product.id}:`, error);
                continue;
            }
        }
    }

    return null;
}

export const automaticallyAssignSubscription = async (childId: string, childEmail: string, parentId: string) => {
    const childUserDocRef = doc(db, 'users', childId);
    const childUserSnap = await getDoc(childUserDocRef);

    if (!childUserSnap.exists()) {
        console.log('Child user document not found');
        return;
    }

    const childData = childUserSnap.data();
    const childGroups = childData.groups || [];

    if (childGroups.length > 0) {
        const matchingSubscription = await findSubscriptionByChildGroups(parentId, childGroups);
        if (matchingSubscription) {
            await assignChildToSubscription(parentId, childId, childEmail, matchingSubscription);
            return;
        }
    }

    const unassignedSubscriptions = await getUnassignedSubscriptions(parentId);

    if (unassignedSubscriptions.length === 1) {
        await assignChildToSubscription(parentId, childId, childEmail, unassignedSubscriptions[0]);
    } else if (unassignedSubscriptions.length > 1) {
        await assignChildToSubscription(parentId, childId, childEmail, unassignedSubscriptions[0]);
    }
};

export const getSubscriptionChild = async (subscriptionId: string) => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }
    const q = query(
        collection(db, 'users', auth.currentUser.uid, 'children'),
        where('subscriptions', 'array-contains', subscriptionId),
    );
    const querySnap = await getDocs(q);
    if (querySnap.empty) {
        throw new Error('db/no-document');
    }
    return querySnap.docs[0].data() as IUser;
};

export const getGroupModulesIds = async (group: string) => {
    const docRef = doc(db, 'groups', group);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return Object.keys(docSnap.data().modules);
};

export const getModule = async (module: string) => {
    const docRef = doc(db, 'modules', module);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return docSnap.data();
};

export const getModulesIds = async () => {
    const modulesIds: string[] = [];
    const q = query(collection(db, 'modules'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        modulesIds.push(docSnap.id);
    });
    return modulesIds;
};

export const getAllModules = async () => {
    const modules: DocumentData[] = [];
    const q = query(collection(db, 'modules'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        modules.push({ id: docSnap.id, ...docSnap.data() });
    });
    return modules;
};

export const getModules = async (moduleIds: string[]) => {
    if (moduleIds.length === 0) {
        return [];
    }

    if (moduleIds.length <= 10) {
        const modules: DocumentData[] = [];
        const q = query(collection(db, 'modules'), where(documentId(), 'in', moduleIds));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            modules.push({ id: docSnap.id, ...docSnap.data() });
        });
        return modules;
    }

    const chunks: string[][] = [];
    for (let i = 0; i < moduleIds.length; i += 10) {
        chunks.push(moduleIds.slice(i, i + 10));
    }

    const chunkPromises = chunks.map(async (chunk: string[]) => {
        try {
            const modules: DocumentData[] = [];
            const q = query(collection(db, 'modules'), where(documentId(), 'in', chunk));
            const querySnap = await getDocs(q);
            querySnap.forEach(docSnap => {
                modules.push({ id: docSnap.id, ...docSnap.data() });
            });
            return modules;
        } catch (error) {
            console.warn('Failed to fetch module chunk:', error);
            return [];
        }
    });

    const chunkResults = await Promise.all(chunkPromises);
    return chunkResults.flat();
};

export const getModuleLesson = async (module: string, lesson: string) => {
    // TODO: Change "topics" to "lessons" once DB migrations is done
    const docRef = doc(db, 'modules', module, 'topics', lesson);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return docSnap.data();
};

export const getGroupModuleLastLessonNumber = async (group: string, module: string) => {
    const docRef = doc(db, 'groups', group);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return Number(docSnap.data().modules[module]);
};

export const getModuleLessons = async (module: string, lesson: number) => {
    const lessons: DocumentData[] = [];
    const docRef = doc(db, 'modules', module);
    // TODO: Change "topics" to "lessons" once DB migrations is done
    const q = query(collection(docRef, 'topics'), where('order', '<=', lesson), orderBy('order'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        lessons.push({ id: docSnap.id, ...docSnap.data() });
    });
    return lessons;
};
